import React from 'react'

import type { DialogProps } from '@paintscout/ui'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@paintscout/ui'

import QuoteTerms from '../../../Quote/QuoteTerms'

const useStyles = makeStyles(
  (theme) => ({
    content: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(2)
    }
  }),
  { name: 'PresentationTermsDialog' }
)

/**
 * onClose is narrowed to a plain callback. MUI types it as `(event, reason) => void`, but
 * @paintscout/ui's Dialog swallows both reasons it can produce (backdropClick, escapeKeyDown), so
 * the Close button is the only caller and it has no reason to report.
 */
export type PresentationTermsDialogProps = Omit<DialogProps, 'onClose'> & { onClose: () => void }

/**
 * Read-only view of the quote's terms, opened from "View Terms" in the presentation's More Actions
 * menu (see PresentationAcceptButton).
 *
 * That menu item only appears when the presentation has no terms block, which is exactly when the
 * terms are either buried in the Quick Cart drawer's own scroll pane or not rendered on screen at
 * all. A dialog works in both cases; scrolling to the `#terms` anchor works in neither.
 *
 * QuoteTerms is deliberately given no isQuoteView/isCustomerView/isPreview flag, so its signature
 * card stays out of the dialog. Signing happens through the Accept flow, not here.
 */
export default function PresentationTermsDialog(props: PresentationTermsDialogProps) {
  const classes = useStyles(props)
  const { onClose, ...otherProps } = props

  return (
    <Dialog maxWidth={'md'} fullWidth onClose={onClose} {...otherProps}>
      <DialogTitle>Terms</DialogTitle>
      <DialogContent className={classes.content}>
        <QuoteTerms id={'terms-dialog'} />
      </DialogContent>
      <DialogActions>
        <Button variant={'contained'} onClick={() => onClose()}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
